import {
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button, Card, CardBody, CardHeader, Input, Link } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { type FC, useEffect, useMemo, useState } from 'react';
import { NoData } from '@/app/components/no-data.tsx';
import OrganizationStaffMemberDialog from '@/app/components/organization/organization-staff-member-dialog.tsx';
import { usePageLoading } from '@/app/components/page-loading.tsx';
import type { AccountModel } from '@/app/types/account.type.ts';
import { useDeleteAccountSiteStaffMemberMutation } from '@/slices/account-site-staff-member-api-slice.ts';
import { useFetchStaffMembersQuery } from '@/slices/staff-api-slice.ts';

export interface AccountStaffMembersProps {
  account: AccountModel;
}

export const AccountStaffMembers: FC<AccountStaffMembersProps> = ({ account }) => {
  const { data, isLoading } = useFetchStaffMembersQuery(
    account?.key ? new URLSearchParams({ accountKey: account.key }).toString() : skipToken,
  );
  const [deleteStaffMember, { isLoading: deleteLoading }] =
    useDeleteAccountSiteStaffMemberMutation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { showPageLoading, hidePageLoading } = usePageLoading();

  useEffect(() => {
    isLoading || deleteLoading
      ? showPageLoading('staff-members')
      : hidePageLoading('staff-members');
  }, [isLoading, deleteLoading]);

  const sortedStaffMembers = useMemo(() => {
    if (!data) {
      return [];
    }
    return [...data].sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [data]);

  const filteredStaffMembers = useMemo(() => {
    if (!searchQuery.trim()) {
      return sortedStaffMembers;
    }

    const query = searchQuery.toLowerCase();
    return sortedStaffMembers.filter(staff => {
      const fullName = `${staff.firstName} ${staff.lastName}`.toLowerCase();
      const email = staff.emailAddress?.toLowerCase() || '';
      const accountSiteStaffMember = staff.accountSiteStaffMembers.find(
        x => x.accountId === account.id,
      );
      const role = accountSiteStaffMember?.staffRole.description.toLowerCase() || '';

      return fullName.includes(query) || email.includes(query) || role.includes(query);
    });
  }, [sortedStaffMembers, searchQuery, account.id]);

  const handleDelete = (staffKey: string) => {
    const staffMember = data?.find(s => s.key === staffKey);
    const accountSiteStaffMember = staffMember?.accountSiteStaffMembers.find(
      x => x.accountId === account.id,
    );
    if (accountSiteStaffMember?.key) {
      deleteStaffMember(accountSiteStaffMember.key);
    }
  };

  return (
    <>
      <OrganizationStaffMemberDialog open={open} setOpen={setOpen} />
      <Card>
        <CardHeader className='flex justify-between items-center px-4 pt-4'>
          <div className='flex items-center gap-2'>
            <UserGroupIcon className='size-5' aria-hidden='true' />
            <h2 className='font-semibold m-0'>Staff Users</h2>
            <div className='h-6 w-6 flex items-center justify-center font-semibold rounded-full bg-default-200 text-default-500 text-sm'>
              {sortedStaffMembers.length}
            </div>
          </div>
          <Button color='primary' variant='bordered' onPress={() => setOpen(true)}>
            <PlusIcon className='size-5' />
            Add User
          </Button>
        </CardHeader>
        <CardBody className='px-4 pb-4'>
          <div className='mb-4'>
            <Input
              placeholder='Search staff users by name, email, or role...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              startContent={<MagnifyingGlassIcon className='size-5 text-default-400' />}
              isClearable
              onClear={() => setSearchQuery('')}
            />
          </div>
          <div className='flex-col'>
            {!filteredStaffMembers.length && (
              <NoData
                message={searchQuery ? 'No staff users match your search' : 'No staff users found'}
              />
            )}
            {filteredStaffMembers.map(staffMember => {
              const accountSiteStaffMember = staffMember.accountSiteStaffMembers.find(
                x => x.accountId === account.id,
              );
              return (
                <div
                  className='flex items-center px-4 py-2 listGroupItem bg-default-50'
                  key={staffMember.id}
                >
                  <div className='flex-1'>
                    <div className='font-medium'>
                      {staffMember.firstName} {staffMember.lastName}
                    </div>
                    {accountSiteStaffMember && (
                      <div className='text-sm text-default-500'>
                        {accountSiteStaffMember.staffRole.description}
                      </div>
                    )}
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      isIconOnly
                      size='sm'
                      variant='flat'
                      color='primary'
                      href={`/staff/edit/${staffMember.key}`}
                      as={Link}
                    >
                      <PencilIcon className='size-4' />
                    </Button>
                    <Button
                      isIconOnly
                      size='sm'
                      color='danger'
                      variant='flat'
                      onPress={() => handleDelete(staffMember.key)}
                    >
                      <XMarkIcon className='size-4' />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
    </>
  );
};
